import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTrack } from './TrackLayout';
import { trackApi, errMsg } from '../../lib/trackApi';
import TaskFilterBar, { filtersToParams } from '../../components/track/TaskFilterBar';
import {
  C, btnGhost, btnSm, btnLink, input as inputStyle, select as selectStyle,
  todayYmd, addDays, diffDays, fmtMD, statusOf, flattenTree,
} from '../../components/track/trackUi';

const ROW_H = 30;
const HEAD_H = 40;
const LEFT_W = 300;
const ZOOMS = { day: 30, week: 12, month: 4 };

function toYmdLocal(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function spanOf(t) {
  const created = toYmdLocal(t.createdAt);
  const start = t.startDate || t.dueDate || created;
  const end = t.dueDate || t.startDate || start;
  return { start, end: end < start ? start : end, undated: !t.startDate && !t.dueDate };
}

export default function TrackGantt() {
  const { boards, members, boardById, openTask, refreshKey } = useTrack();
  const today = todayYmd();
  const [filters, setFilters] = useState({ boardId: null, assigneeId: null, initiatorId: null, status: '', q: '', starred: false, includeDone: false });
  const [from, setFrom] = useState(addDays(today, -7));
  const [days, setDays] = useState(42);
  const [zoom, setZoom] = useState('day');
  const [groupByBoard, setGroupByBoard] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [error, setError] = useState('');
  const [hover, setHover] = useState(null);

  const to = addDays(from, days - 1);
  const dayW = ZOOMS[zoom];

  const load = useCallback(async () => {
    try {
      const d = await trackApi.gantt({ ...filtersToParams(filters), from, to });
      setTasks(d?.data || []);
      setError('');
    } catch (e) {
      setError(errMsg(e));
    }
  }, [filters, from, to]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, refreshKey]);

  const rows = useMemo(() => {
    const out = [];
    if (groupByBoard) {
      const byBoard = new Map();
      tasks.forEach((t) => {
        if (!byBoard.has(t.boardId)) byBoard.set(t.boardId, []);
        byBoard.get(t.boardId).push(t);
      });
      boards.filter((b) => byBoard.has(b.id)).forEach((b) => {
        const key = `b${b.id}`;
        out.push({ kind: 'board', key, board: b, count: byBoard.get(b.id).length });
        if (!collapsed.has(key)) flattenTree(byBoard.get(b.id), collapsed).forEach((r) => out.push({ kind: 'task', key: r.task.id, ...r }));
      });
    } else {
      flattenTree(tasks, collapsed).forEach((r) => out.push({ kind: 'task', key: r.task.id, ...r }));
    }
    return out;
  }, [tasks, boards, collapsed, groupByBoard]);

  const toggle = (key) => setCollapsed((s) => {
    const n = new Set(s);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  const width = days * dayW;
  const height = HEAD_H + rows.length * ROW_H;
  const x = (ymd) => diffDays(from, ymd) * dayW;

  const ticks = useMemo(() => {
    const list = [];
    for (let i = 0; i < days; i += 1) {
      const ymd = addDays(from, i);
      const dt = new Date(`${ymd}T00:00:00Z`);
      list.push({ ymd, i, dow: dt.getUTCDay(), dom: dt.getUTCDate(), month: dt.getUTCMonth() + 1 });
    }
    return list;
  }, [from, days]);

  const shift = (n) => setFrom(addDays(from, n));

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <button type="button" style={{ ...btnGhost, ...btnSm }} onClick={() => shift(-Math.max(7, Math.floor(days / 2)))}>‹</button>
        <input type="date" style={inputStyle} value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} />
        <button type="button" style={{ ...btnGhost, ...btnSm }} onClick={() => shift(Math.max(7, Math.floor(days / 2)))}>›</button>
        <button type="button" style={{ ...btnGhost, ...btnSm }} onClick={() => setFrom(addDays(today, -7))}>今天</button>
        <select style={selectStyle} value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={14}>2 週</option>
          <option value={42}>6 週</option>
          <option value={90}>3 個月</option>
          <option value={180}>6 個月</option>
        </select>
        <select style={selectStyle} value={zoom} onChange={(e) => setZoom(e.target.value)}>
          <option value="day">日</option>
          <option value="week">週</option>
          <option value="month">月</option>
        </select>
        <label style={{ fontSize: 13, display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          <input type="checkbox" checked={groupByBoard} onChange={(e) => setGroupByBoard(e.target.checked)} />
          依看板分組
        </label>
      </div>
      <div style={{ marginBottom: 10 }}>
        <TaskFilterBar boards={boards} members={members} value={filters} onChange={setFilters} />
      </div>
      {error && <div style={{ color: C.danger, marginBottom: 8 }}>{error}</div>}

      <div style={{ display: 'flex', border: `1px solid ${C.border}`, borderRadius: 8, maxHeight: '70vh', overflowY: 'auto', background: '#fff' }}>
        <div style={{ width: LEFT_W, flexShrink: 0, borderRight: `1px solid ${C.border}` }}>
          <div style={{ height: HEAD_H, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', padding: '0 8px', fontSize: 12, color: C.muted }}>
            任務（{tasks.length}）
          </div>
          {rows.map((r) => (r.kind === 'board' ? (
            <div key={r.key} style={{ height: ROW_H, display: 'flex', alignItems: 'center', padding: '0 8px', background: '#f9fafb', fontWeight: 700, fontSize: 13, borderBottom: '1px solid #f3f4f6' }}>
              <button type="button" style={{ ...btnLink, color: C.muted, width: 16 }} onClick={() => toggle(r.key)}>{collapsed.has(r.key) ? '▸' : '▾'}</button>
              {r.board.name}<span style={{ color: C.muted, fontWeight: 400, marginLeft: 4 }}>（{r.count}）</span>
            </div>
          ) : (
            <div
              key={r.key}
              onMouseEnter={() => setHover(r.task.id)}
              onMouseLeave={() => setHover(null)}
              style={{
                height: ROW_H, display: 'flex', alignItems: 'center', gap: 4, paddingLeft: 8 + r.depth * 16, paddingRight: 8,
                fontSize: 12, borderBottom: '1px solid #f3f4f6', background: hover === r.task.id ? C.brandSoft : undefined,
              }}
            >
              {r.hasChildren
                ? <button type="button" style={{ ...btnLink, color: C.muted, width: 14 }} onClick={() => toggle(r.task.id)}>{collapsed.has(r.task.id) ? '▸' : '▾'}</button>
                : <span style={{ width: 14, color: '#d1d5db' }}>{r.depth ? '└' : ''}</span>}
              <span style={{ color: C.muted, width: 48, flexShrink: 0 }}>{r.task.taskNo}</span>
              <button
                type="button"
                onClick={() => openTask(r.task.id)}
                title={r.task.title}
                style={{ ...btnLink, color: '#111', fontSize: 12, fontWeight: r.depth ? 400 : 700, flex: 1, minWidth: 0, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {r.task.isStarred ? '★ ' : ''}{r.task.title}
              </button>
              <span style={{ color: C.muted, flexShrink: 0 }}>{r.task.assigneeName}</span>
            </div>
          )))}
        </div>

        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', flex: 1, minWidth: 0 }}>
          <svg width={width} height={Math.max(height, HEAD_H + ROW_H)} style={{ display: 'block' }}>
            {ticks.map((t) => {
              const weekend = t.dow === 0 || t.dow === 6;
              const showLabel = zoom === 'day' || (zoom === 'week' && t.dow === 1) || (zoom === 'month' && t.dom === 1);
              return (
                <g key={t.ymd}>
                  {weekend && zoom !== 'month' && <rect x={t.i * dayW} y={HEAD_H} width={dayW} height={height - HEAD_H} fill="#fafafa" />}
                  {(t.dom === 1 || t.i === 0) && (
                    <text x={t.i * dayW + 3} y={14} fontSize={11} fill={C.muted} fontWeight={700}>{t.month} 月</text>
                  )}
                  {showLabel && (
                    <>
                      <line x1={t.i * dayW} x2={t.i * dayW} y1={HEAD_H - 14} y2={height} stroke="#f0f0f0" />
                      <text x={t.i * dayW + (zoom === 'day' ? dayW / 2 : 2)} y={HEAD_H - 6} fontSize={10} fill={weekend ? '#ef4444' : C.muted} textAnchor={zoom === 'day' ? 'middle' : 'start'}>
                        {zoom === 'month' ? `${t.month}/1` : (zoom === 'week' ? `${t.month}/${t.dom}` : t.dom)}
                      </text>
                    </>
                  )}
                </g>
              );
            })}
            <line x1={0} x2={width} y1={HEAD_H} y2={HEAD_H} stroke={C.border} />

            {rows.map((r, idx) => {
              const y = HEAD_H + idx * ROW_H;
              if (r.kind === 'board') return <rect key={r.key} x={0} y={y} width={width} height={ROW_H} fill="#f9fafb" />;
              const t = r.task;
              const board = boardById.get(t.boardId);
              const st = statusOf(board, t.status);
              const { start, end, undated } = spanOf(t);
              if (!start) return null;
              const x1 = Math.max(x(start), -dayW);
              const x2 = Math.min(x(end) + dayW, width + dayW);
              if (x2 < 0 || x1 > width) return null;
              const overdue = !st.isDone && t.dueDate && t.dueDate < today;
              const bw = Math.max(x2 - x1, 6);
              return (
                <g
                  key={r.key}
                  style={{ cursor: 'pointer' }}
                  onClick={() => openTask(t.id)}
                  onMouseEnter={() => setHover(t.id)}
                  onMouseLeave={() => setHover(null)}
                >
                  {hover === t.id && <rect x={0} y={y} width={width} height={ROW_H} fill={C.brandSoft} opacity={0.6} />}
                  <rect
                    x={x1}
                    y={y + 7}
                    width={bw}
                    height={ROW_H - 14}
                    rx={4}
                    fill={st.color}
                    opacity={st.isDone ? 0.45 : (undated ? 0.35 : 0.9)}
                    stroke={overdue ? C.danger : (undated ? st.color : 'none')}
                    strokeWidth={overdue ? 2 : 1}
                    strokeDasharray={undated ? '3 2' : undefined}
                  />
                  {bw > 60 && (
                    <text x={x1 + 6} y={y + ROW_H / 2 + 4} fontSize={11} fill="#fff" style={{ pointerEvents: 'none' }}>
                      {st.label}{t.assigneeName ? `・${t.assigneeName}` : ''}
                    </text>
                  )}
                  <title>
                    {`${t.taskNo} ${t.title}\n狀態：${st.label}\n執行人：${t.assigneeName || '未指派'}\n${fmtMD(t.startDate) || '?'} ～ ${fmtMD(t.dueDate) || '?'}${overdue ? '（逾期）' : ''}${t.lastNote ? `\n備註：${t.lastNote}` : ''}`}
                  </title>
                </g>
              );
            })}

            {today >= from && today <= to && (
              <g>
                <line x1={x(today) + dayW / 2} x2={x(today) + dayW / 2} y1={HEAD_H - 16} y2={height} stroke={C.danger} strokeWidth={1.5} />
                <text x={x(today) + dayW / 2 + 3} y={HEAD_H - 18} fontSize={10} fill={C.danger}>今天</text>
              </g>
            )}
          </svg>
        </div>
      </div>
      {!tasks.length && <div style={{ color: C.muted, padding: '12px 0' }}>此期間沒有任務</div>}
    </div>
  );
}
